/**
 * Logs tab — admin-facing agent <-> BE audit log.
 *
 * Surfaces "did this NV's agent ever register? when did it last heartbeat?
 * are we seeing auth_failed spikes from one IP?" — the kind of question
 * that previously required SQL + RDP to answer.
 *
 * Backend writes one row per:
 *   - register / register_failed (every /agent/register call)
 *   - auth_failed (every 401 from requireAgent)
 *   - heartbeat (sampled 1/12 in the BE)
 *   - screenshot_skip (when a phash-dedup hit happens)
 *
 * Filters: free-text search, event-type chips, ok toggle. Auto-refreshes
 * every 30s so admins see new rows without F5.
 */
import { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import {
  AlertTriangle,
  CheckCircle2,
  LogOut,
  Search,
  ScrollText,
  ShieldAlert,
  UserPlus,
  Wifi,
  XCircle,
} from 'lucide-react'
import { Button, buttonVariants } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { Spinner } from '@/components/ui/spinner'
import { connectionEventsService } from '@/services/connection-events.service'
import { formatRelativeTime } from '@/lib/format'
import type { AgentConnectionEvent, AgentConnectionEventType } from '@monitor/shared'

type EventFilter = 'all' | AgentConnectionEventType
type StatusFilter = 'all' | 'ok' | 'fail'

const TYPE_CHIPS: { key: EventFilter; label: string; icon: React.ReactNode }[] = [
  { key: 'all', label: 'Tất cả', icon: <ScrollText className="h-3 w-3" /> },
  { key: 'register', label: 'register', icon: <UserPlus className="h-3 w-3" /> },
  { key: 'register_failed', label: 'register_failed', icon: <XCircle className="h-3 w-3" /> },
  { key: 'auth_failed', label: 'auth_failed', icon: <ShieldAlert className="h-3 w-3" /> },
  { key: 'heartbeat', label: 'heartbeat', icon: <Wifi className="h-3 w-3" /> },
  { key: 'screenshot_skip', label: 'screenshot_skip', icon: <AlertTriangle className="h-3 w-3" /> },
  { key: 'disconnect', label: 'disconnect', icon: <LogOut className="h-3 w-3" /> },
]

export default function LogsTab() {
  const [search, setSearch] = useState('')
  const [typeFilter, setTypeFilter] = useState<EventFilter>('all')
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all')
  const [page, setPage] = useState(1)

  const { data, isLoading, isFetching } = useQuery({
    queryKey: ['connection-events', page],
    queryFn: () =>
      connectionEventsService.list({
        page,
        pageSize: 100,
      }),
    refetchInterval: 30_000,
  })

  // Reset page when a filter changes — otherwise the user could be sitting
  // on page 5 of "heartbeat" then switch to "auth_failed" and see an
  // empty page because there are only 3 failures.
  const filtered = useMemo(() => {
    if (!data) return []
    const q = search.trim().toLowerCase()
    return data.items.filter((row) => {
      if (typeFilter !== 'all' && row.eventType !== typeFilter) return false
      if (statusFilter === 'ok' && !row.ok) return false
      if (statusFilter === 'fail' && row.ok) return false
      if (q) {
        const hay =
          `${row.employeeCode ?? ''} ${row.hostname ?? ''} ${row.agentVersion ?? ''} ` +
          `${row.error ?? ''} ${row.ipAddress ?? ''}`.toLowerCase()
        if (!hay.includes(q)) return false
      }
      return true
    })
  }, [data, search, typeFilter, statusFilter])

  const totalPages = data ? Math.max(1, Math.ceil(data.total / 100)) : 1

  return (
    <div className="container space-y-4 py-4">
      {/* KPI cards */}
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <KpiCard
          label="Tổng event"
          value={data?.summary.total ?? 0}
          icon={<ScrollText className="h-4 w-4" />}
        />
        <KpiCard
          label="Lỗi"
          value={data?.summary.failures ?? 0}
          tone="danger"
          icon={<XCircle className="h-4 w-4" />}
        />
        <KpiCard
          label="Register gần nhất"
          value={data?.summary.lastRegisterAt ?? '—'}
          tone="default"
          icon={<UserPlus className="h-4 w-4" />}
          isText
        />
        <KpiCard
          label="Auth fail gần nhất"
          value={data?.summary.lastAuthFailedAt ?? '—'}
          tone={data?.summary.lastAuthFailedAt ? 'warning' : 'default'}
          icon={<ShieldAlert className="h-4 w-4" />}
          isText
        />
      </div>

      <Card>
        <CardHeader className="flex flex-col gap-2 space-y-0 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between">
          <CardTitle>
            Audit log{' '}
            {isFetching && (
              <span className="ml-2 text-xs text-muted-foreground">(đang tải…)</span>
            )}
          </CardTitle>
          <div className="flex flex-wrap gap-2">
            <div className="relative">
              <Search className="absolute left-2 top-2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Tìm NV, IP, hostname, lỗi…"
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value)
                  setPage(1)
                }}
                className="w-56 pl-8"
              />
            </div>
            <div className="flex gap-1 rounded-md border p-0.5">
              {(['all', 'ok', 'fail'] as const).map((s) => (
                <Button
                  key={s}
                  size="sm"
                  variant={statusFilter === s ? 'default' : 'ghost'}
                  onClick={() => {
                    setStatusFilter(s)
                    setPage(1)
                  }}
                >
                  {statusLabel(s)}
                </Button>
              ))}
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          {/* Type filter chips */}
          <div className="flex flex-wrap gap-1">
            {TYPE_CHIPS.map((c) => (
              <Button
                key={c.key}
                size="sm"
                variant={typeFilter === c.key ? 'default' : 'outline'}
                onClick={() => {
                  setTypeFilter(c.key)
                  setPage(1)
                }}
                className="h-7 gap-1 px-2 text-xs"
              >
                {c.icon}
                {c.label}
              </Button>
            ))}
          </div>

          {isLoading ? (
            <div className="flex h-32 items-center justify-center">
              <Spinner />
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="border-b text-left text-xs text-muted-foreground">
                    <tr>
                      <th className="p-2">Lúc</th>
                      <th className="p-2">Event</th>
                      <th className="p-2">NV</th>
                      <th className="p-2">Hostname</th>
                      <th className="p-2">Version</th>
                      <th className="p-2">IP</th>
                      <th className="p-2">Chi tiết</th>
                      <th className="p-2 text-right">Trạng thái</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map((row) => (
                      <Row key={row.id} row={row} />
                    ))}
                    {filtered.length === 0 && (
                      <tr>
                        <td colSpan={8} className="p-4 text-center text-muted-foreground">
                          Không có event nào khớp bộ lọc.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              <div className="flex items-center justify-between text-xs text-muted-foreground">
                <span>
                  Trang {page}/{totalPages} · {data?.total ?? 0} event
                </span>
                <div className="flex gap-1">
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={page <= 1}
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                  >
                    Trước
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={page >= totalPages}
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  >
                    Sau
                  </Button>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  )
}

function Row({ row }: { row: AgentConnectionEvent }) {
  return (
    <tr className="border-b hover:bg-muted/40">
      <td className="p-2 text-xs whitespace-nowrap">
        {formatRelativeTime(row.createdAt)}
      </td>
      <td className="p-2">
        <EventBadge type={row.eventType} />
      </td>
      <td className="p-2 font-mono text-xs">
        {row.employeeId ? (
          <Link
            to={`/diagnostics/${row.employeeId}`}
            className={buttonVariants({ variant: 'link', size: 'sm', className: 'h-auto p-0' })}
          >
            {row.employeeCode ?? `#${row.employeeId}`}
          </Link>
        ) : (
          <span className="text-muted-foreground">{row.employeeCode ?? '—'}</span>
        )}
      </td>
      <td className="p-2 font-mono text-xs">{row.hostname ?? '—'}</td>
      <td className="p-2 text-xs">{row.agentVersion ?? '—'}</td>
      <td className="p-2 font-mono text-xs">{row.ipAddress ?? '—'}</td>
      <td className="p-2 max-w-[280px] text-xs">
        {row.error ? (
          <span className="text-red-600" title={row.error}>
            {row.error}
          </span>
        ) : row.userAgent ? (
          <span className="text-muted-foreground" title={row.userAgent}>
            {truncateUa(row.userAgent)}
          </span>
        ) : (
          <span className="text-muted-foreground">—</span>
        )}
      </td>
      <td className="p-2 text-right">
        {row.ok ? (
          <Badge variant="success">
            <CheckCircle2 className="mr-1 h-3 w-3" />
            OK
          </Badge>
        ) : (
          <Badge variant="destructive">
            <XCircle className="mr-1 h-3 w-3" />
            Lỗi
          </Badge>
        )}
      </td>
    </tr>
  )
}

function EventBadge({ type }: { type: AgentConnectionEventType }) {
  switch (type) {
    case 'register':
      return <Badge variant="success">register</Badge>
    case 'register_failed':
      return <Badge variant="destructive">register_failed</Badge>
    case 'auth_failed':
      return <Badge variant="destructive">auth_failed</Badge>
    case 'heartbeat':
      return <Badge variant="secondary">heartbeat</Badge>
    case 'screenshot_skip':
      return <Badge variant="warning">screenshot_skip</Badge>
    case 'disconnect':
      return <Badge variant="secondary">disconnect</Badge>
  }
}

function statusLabel(s: StatusFilter): string {
  switch (s) {
    case 'all':
      return 'Tất cả'
    case 'ok':
      return 'OK'
    case 'fail':
      return 'Lỗi'
  }
}

function truncateUa(ua: string): string {
  return ua.length > 60 ? `${ua.slice(0, 57)}…` : ua
}

function KpiCard({
  label,
  value,
  icon,
  tone = 'default',
  isText = false,
}: {
  label: string
  value: number | string
  icon: React.ReactNode
  tone?: 'default' | 'success' | 'warning' | 'danger'
  isText?: boolean
}) {
  const toneClass = {
    default: 'text-foreground',
    success: 'text-green-600',
    warning: 'text-amber-600',
    danger: 'text-red-600',
  }[tone]
  return (
    <Card>
      <CardContent className="flex items-center justify-between p-4">
        <div>
          <div className="text-xs text-muted-foreground">{label}</div>
          <div className={`font-semibold ${toneClass} ${isText ? 'text-sm' : 'text-2xl'}`}>
            {typeof value === 'number' ? value : value === '—' ? value : formatRelativeTime(value)}
          </div>
        </div>
        <div className="text-muted-foreground">{icon}</div>
      </CardContent>
    </Card>
  )
}
